import type { ImageSearchIds, ImageSearchRequest } from '@elara/client-core/lib/image-search-api';
import type { WidgetImage } from '@elara/domain/models/schemas/image-schemas';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { convertToWidgetImages, fetchImages } from '@elara/client-core/lib/image-search-api';
import { cn } from '@elara/client-core/lib/utils';
import { assert } from '@elara/core/errors';
import { ImageSearchIdsSchema } from '@elara/domain/models/schemas/widget-schemas';

import { sentry } from '#lib/sentry.js';
import { Alert, AlertDescription, IconSizes, MaterialIcon } from '#ui/index.js';

import { ImageGalleryWidget } from './ImageGalleryWidget.js';
import { WidgetErrorState } from './WidgetErrorState.js';
import { WidgetLoadingState } from './WidgetLoadingState.js';

export interface ImageSearchWidgetProps {
  className?: string;
  ids?: ImageSearchIds | null;
  tags?: string[];
  sourceFilters?: Array<'google' | 'tripadvisor' | 'sabre'>;
  limit?: number;
  round?: 'sm' | 'md' | 'lg' | 'xl';
  displayCount?: boolean;
  fallback?: () => React.ReactNode;
  onImagesLoaded?: (images: Array<{ id: string; url: string; caption?: string; alt?: string }>) => void;
}

type SearchState = 'idle' | 'loading' | 'success' | 'error';

/**
 * ImageSearchWidget automatically searches for images using provided IDs
 * and displays results using the ImageGallery component.
 * If no IDs are provided, the widget is hidden.
 */
export function ImageSearchWidget({
  className,
  ids: ids = {},
  tags = [],
  sourceFilters = [],
  limit = 5,
  round = 'lg',
  displayCount = false,
  fallback,
  onImagesLoaded,
}: ImageSearchWidgetProps) {
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [images, setImages] = useState<WidgetImage[]>([]);

  // Check if any IDs are provided
  const hasIds = useMemo(() => {
    const result = ImageSearchIdsSchema.safeParse(ids);
    return result.success;
  }, [ids]);

  const performSearch = useCallback(
    async (ids: ImageSearchIds | null) => {
      setSearchState('loading');

      try {
        assert(ids, 'No IDs provided');
        const request: ImageSearchRequest = { ids, preferWebp: true, validate: 'imgur', tags };

        if (sourceFilters.length > 0) {
          request.sourceFilters = sourceFilters;
        }

        const response = await fetchImages(request);
        const widgetImages = convertToWidgetImages(response.images);

        setImages(widgetImages);
        setSearchState('success');
        onImagesLoaded?.(widgetImages);
      } catch (err) {
        sentry?.captureException(err);
        setSearchState('error');
      }
    },
    [onImagesLoaded, tags, sourceFilters],
  );

  // Automatically search when component mounts with IDs
  useEffect(() => {
    if (hasIds && searchState === 'idle') {
      void performSearch(ids);
    }
  }, [hasIds, ids, performSearch, searchState]);

  // If no IDs provided, don't render anything
  if (!hasIds) {
    return undefined;
  }

  // Render loading state
  if (searchState === 'loading') {
    return <WidgetLoadingState message="Loading images" className={className} />;
  }

  // Render error state
  if (searchState === 'error') {
    return <WidgetErrorState message="Failed to fetch images" className={className} />;
  }

  // Render success state with images
  if (searchState === 'success' && images.length > 0) {
    return (
      <ImageGalleryWidget
        images={images}
        maxImages={limit}
        className={className}
        round={round}
        displayCount={displayCount}
      />
    );
  }

  if (searchState === 'success' && images.length === 0) {
    if (fallback) {
      return <>{fallback()}</>;
    }

    return (
      <div className={cn('w-full', className)}>
        <Alert className="flex items-center gap-2">
          <MaterialIcon icon="image_search" size={IconSizes.sm} />
          <AlertDescription>No images available</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Default state (no images to show)
  return undefined;
}
